import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { FiCheckCircle, FiXCircle, FiX, FiPlusCircle } from "react-icons/fi";

export default function AddResidency({ theme }) {
  const [residencyName, setResidencyName] = useState("");
  const [status, setStatus] = useState("Active");
  const [toast, setToast] = useState({ type: "", message: "" });

  // Auto‐dismiss toast after 5 seconds
  useEffect(() => {
    if (toast.message) {
      const timer = setTimeout(() => setToast({ type: "", message: "" }), 5000);
      return () => clearTimeout(timer);
    }
  }, [toast]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setToast({ type: "", message: "" });

    if (!residencyName.trim()) {
      setToast({ type: "error", message: "Residency name cannot be empty." });
      return;
    }

    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/add-residency`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${localStorage.getItem("token")}`  },
          body: JSON.stringify({
            residency: residencyName.trim(),
            status: status === "Active" ? 1 : 0,
          }),
        }
      );

      const data = await response.json();

      if (response.ok) {
        setToast({ type: "success", message: data.message || "Residency added successfully!" });
        setResidencyName("");
        setStatus("Active");
      } else {
        setToast({ type: "error", message: data.message || "Failed to add residency." });
      }
    } catch (error) {
      console.error("Fetch error:", error);
      setToast({ type: "error", message: "Network error. Please try again." });
    }
  };

  return (
    <div className="relative bg-white rounded-lg shadow-sm border border-gray-200 p-6 max-w-2xl">
      {/* Toast Notifications */}
      <div className="fixed top-6 right-6 z-50">
        <AnimatePresence>
          {toast.message && (
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 50 }}
              transition={{ duration: 0.3 }}
              className={`flex items-center max-w-xs p-4 border rounded-lg shadow-lg space-x-3 overflow-hidden
                ${toast.type === "error"
                  ? "bg-red-50 border-red-300 text-red-700"
                  : "bg-green-50 border-green-300 text-green-700"}`}
            >
              <div className="shrink-0">
                {toast.type === "error" ? (
                  <FiXCircle size={20} />
                ) : (
                  <FiCheckCircle size={20} />
                )}
              </div>
              <div className="flex-1 text-sm leading-snug">{toast.message}</div>
              <button
                onClick={() => setToast({ type: "", message: "" })}
                className="text-gray-400 hover:text-gray-600 focus:outline-none"
                aria-label="Close"
              >
                <FiX size={16} />
              </button>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Header */}
      <motion.h2
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        className="text-2xl font-bold text-gray-800 mb-6"
      >
        <FiPlusCircle className="inline mr-2 text-blue-600" size={24} />
        Add New Residency
      </motion.h2>

      {/* Form */}
      <motion.form
        onSubmit={handleSubmit}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.2, duration: 0.4 }}
        className="space-y-6"
      >
        {/* Residency Name */}
        <div>
          <label htmlFor="residencyName" className="block text-gray-700 font-medium mb-2">
            Residency Name
          </label>
          <input
            id="residencyName"
            type="text"
            value={residencyName}
            onChange={(e) => setResidencyName(e.target.value)}
            placeholder="e.g. EU (Pre-Settled Status)"
            required
            className="w-full border border-gray-300 rounded-lg px-5 py-3 text-lg placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors"
          />
        </div>

        {/* Status Toggle */}
        <div>
          <span className="block text-gray-700 font-medium mb-2">Status</span>
          <div className="flex space-x-4">
            {['Active', 'Inactive'].map(option => (
              <label
                key={option}
                className={`flex-1 cursor-pointer rounded-lg px-4 py-2 text-center font-medium border transition-colors
                  ${status === option
                    ? option === 'Active'
                      ? 'bg-green-100 border-green-300 text-green-800'
                      : 'bg-red-100 border-red-300 text-red-800'
                    : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'}`}
              >
                <input
                  type="radio"
                  name="status"
                  value={option}
                  checked={status === option}
                  onChange={() => setStatus(option)}
                  className="hidden"
                />
                {option}
              </label>
            ))}
          </div>
        </div>

        {/* Submit Button */}
        <div className="flex justify-end">
          <button
            type="submit"
            className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white font-semibold px-6 py-3 rounded-lg shadow-md transition-colors focus:outline-none focus:ring-2 focus:ring-blue-300"
          >
            Add Residency
          </button>
        </div>
      </motion.form>
    </div>
  );
}
